import type {Row} from 'src/types/byod';

/**
 * One discrete keyboard-navigation "stop" — either a compatible tile row
 * the user can land in, or a `new-row-*` band where dropping creates a new
 * row. Up/Down walks this list in order.
 */
export type KeyboardStop =
  | {kind: 'row'; rowId: string}
  | {kind: 'new-row'; afterIndex: number};

/**
 * Build the up/down navigation list for a keyboard drag. Pure function so
 * the branching (row inclusion, capacity filter, new-row interleaving) is
 * directly testable without `@dnd-kit` or DOM. Lives in its own module
 * (separate from `use-byod-keyboard-nav.ts`) so tests can import it
 * without pulling `@dnd-kit/react` — that package ships ESM that Jest's
 * default transformer can't load.
 */
export function buildKeyboardStops(args: {
  rowOrder: string[];
  rows: Record<string, Row>;
  expectedRowType: 'kpi' | 'viz';
  sourceRowId: string;
  maxTilesPerRow: number;
}): KeyboardStop[] {
  const {rowOrder, rows, expectedRowType, sourceRowId, maxTilesPerRow} = args;
  const stops: KeyboardStop[] = [];
  // New-row band before the first row.
  stops.push({kind: 'new-row', afterIndex: -1});
  for (let i = 0; i < rowOrder.length; i++) {
    const rid = rowOrder[i];
    const r = rows[rid];
    if (
      r &&
      r.type === expectedRowType &&
      r.tiles.length > 0 &&
      // Skip rows that can't accept the source (full from another row).
      // The source's own row is always included regardless of capacity —
      // it has the source tile in it already, so reordering within is fine.
      !(r.tiles.length >= maxTilesPerRow && rid !== sourceRowId)
    ) {
      stops.push({kind: 'row', rowId: rid});
    }
    stops.push({kind: 'new-row', afterIndex: i});
  }
  return stops;
}
